import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../context/AuthContext';
import { AdobeAnalyticsUserRole } from './AdobeAnalyticsUserRole';
import getEnvVar from '@/utils/getEnvVar';
import { throttle } from 'lodash-es';
import { AdobeAnalyticsConst } from './adobeAnalyticsConstants/adobeAnalyticsConstants';
import { PageScrollDetails, PageViewProps } from './types/AdobeAnalyticsTypes';
import { useEventTracker } from './eventTracker/useEventTracker';
import { getCommonPagePayload } from './commonPagePayload/getCommonPagePayload';
import useBreadcrumbs from '@/hooks/useBreadcrumbs';
import { AppRoutes } from '@/utils';
import { getMainCategoryFromPath } from './mainCategoryFromPath/pathUtils';
import { getRegionFromCountries } from './utils/regionUtils';

window.oneDataLayer = window.oneDataLayer || [];

const isAppRoute = (value: string): value is AppRoutes => Object.values<string>(AppRoutes).includes(value);

export const usePageView = () => {
  const { i18n } = useTranslation();
  const { userInfo, isLoggedIn } = useAuthContext();
  const { getBreadcrumb } = useBreadcrumbs();

  const getCategoryTree = useCallback(
    (pageViewData: PageViewProps): string => {
      if (pageViewData.categoryTree) return pageViewData.categoryTree;
      const { pathname } = pageViewData.locationInfo;
      const route = isAppRoute(pathname) ? pathname : AppRoutes.CUSTOMER_FOCUS;
      return getBreadcrumb(route, pageViewData.pageIdentifier)?.label.trim() ?? '';
    },
    [getBreadcrumb],
  );

  const buildPayload = useCallback(
    (pageViewData: PageViewProps) => ({
      timestamp: new Date().toISOString(),
      page: {
        siteName: AdobeAnalyticsConst.SITE_DETAILS.name,
        sysEnv: getEnvVar('MODE'),
        language: i18n.language,
        url: pageViewData.locationInfo.url,
        domain: pageViewData.locationInfo.hostname,
        path: pageViewData.locationInfo.pathname,
        pageTitle: document.title,
        pageName: pageViewData.pageIdentifier,
        referrer: document.referrer,
        market: getRegionFromCountries(userInfo?.countries ?? []),
        mainCategory: pageViewData.mainCategory ?? getMainCategoryFromPath(pageViewData.locationInfo.pathname),
        categoryTree: getCategoryTree(pageViewData),
      },
      user: {
        consent: { C0001: true, C0002: true, C0003: true, C0004: true },
        isLoggedIn,
        account: { intEm: true },
        position: userInfo?.adobeAnalyticsRole ?? AdobeAnalyticsUserRole.ANONYMOUS,
      },
    }),
    [i18n.language, userInfo, isLoggedIn, getCategoryTree],
  );

  const trackEvent = useEventTracker<PageViewProps>(AdobeAnalyticsConst.EVENTS.DEFAULTPAGEVIEW, buildPayload);

  return { trackPageView: trackEvent };
};

export const usePageScroll = () => {
  const { i18n } = useTranslation();

  const buildPayload = useCallback(
    (scrollDetails: PageScrollDetails) => ({
      timestamp: new Date().toISOString(),
      page: {
        ...getCommonPagePayload(scrollDetails.locationInfo, scrollDetails.pageIdentifier),
        scrolled: scrollDetails.scrollPercentage,
        language: i18n.language,
      },
    }),
    [i18n.language],
  );

  const rawTrackEvent = useEventTracker(AdobeAnalyticsConst.EVENTS.PAGESCROLL, buildPayload);

  const throttledTrackEvent = useMemo(
    () => throttle(rawTrackEvent, 500, { leading: true, trailing: true }),
    [rawTrackEvent],
  );

  return { trackScrollEvent: throttledTrackEvent };
};
